import { useState } from 'react';
import { AlertCircle, CheckCircle2, Circle, LayoutGrid, List } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { InputSlot, SectionProgress, SheetProgress } from '@/reports/inputs';
import { focusSlot } from './fillContext';

/**
 * Two ways of showing how far the sheet is filled in, side by side until one
 * is chosen: a list of sections with counts, or a grid of squares, one per
 * thing to fill in. The choice is remembered in this browser.
 */
export type ProgressStyle = 'list' | 'grid';
const STYLE_KEY = 'veriform.reportsV2.progressStyle';

function rememberedStyle(): ProgressStyle {
  try {
    return window.localStorage.getItem(STYLE_KEY) === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

const jumpToSection = (id: string) => document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

function SectionIcon({ section }: { section: SectionProgress }) {
  if (section.total > 0 && section.filled === section.total) return <CheckCircle2 className="size-4 text-emerald-600" />;
  if (section.slots.some((s) => s.required && !s.filled)) return <AlertCircle className="size-4 text-red-600" />;
  return <Circle className="size-4 text-muted-foreground" />;
}

/** Variant 1: the sections, each with its count, and a bar for the whole sheet. */
function ProgressList({ progress }: { progress: SheetProgress }) {
  return (
    <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
      {progress.sections.map((s) => (
        <li key={s.id}>
          <button type="button" onClick={() => jumpToSection(s.id)} className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-muted">
            <SectionIcon section={s} />
            <span className="min-w-0 flex-1 truncate">{s.title}</span>
            <span className="text-xs text-muted-foreground tabular-nums">{s.filled}/{s.total}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function Square({ slot }: { slot: InputSlot }) {
  return (
    <button
      type="button"
      title={`${slot.label}${slot.filled ? '' : slot.required ? ` — ${I18n.get('rv2NeededToPublish')}` : ` — ${I18n.get('rv2EmptyState')}`}`}
      aria-label={slot.label}
      onClick={() => focusSlot(slot)}
      className={cn(
        'size-3 rounded-[3px] border-[1px] outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
        slot.filled ? 'border-sky-800 bg-sky-800' : slot.required ? 'border-red-500 bg-red-50' : 'border-input bg-background',
      )}
    />
  );
}

/** Variant 2: a square per thing to fill in, grouped by section, a sweep's squares by its rows. Click one to go to it. */
function ProgressGrid({ progress }: { progress: SheetProgress }) {
  return (
    <div className="flex flex-col gap-2">
      {progress.sections.map((s) => {
        const groups: InputSlot[][] = [];
        for (const slot of s.slots) {
          const last = groups[groups.length - 1];
          if (last && slot.group !== undefined && last[0].group === slot.group) last.push(slot);
          else groups.push([slot]);
        }
        return (
          <div key={s.id} className="grid grid-cols-[minmax(0,7rem)_1fr] items-start gap-2">
            <button type="button" onClick={() => jumpToSection(s.id)} className="truncate pt-px text-left text-xs hover:underline">{s.title}</button>
            <div className="flex flex-wrap gap-x-1.5 gap-y-1">
              {s.total === 0 ? <span className="text-xs text-muted-foreground">—</span> : groups.map((g, i) => (
                <span key={i} className="flex gap-0.5">{g.map((slot) => <Square key={`${slot.input}:${slot.cell ?? ''}:${slot.field ?? ''}`} slot={slot} />)}</span>
              ))}
            </div>
          </div>
        );
      })}
      <div className="mt-1 flex flex-wrap gap-x-[0.75rem] gap-y-1 border-t-[1px] pt-2 text-[0.7rem] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="size-2.5 rounded-[2px] bg-sky-800" /> {I18n.get('rv2Filled')}</span>
        <span className="flex items-center gap-1"><span className="size-2.5 rounded-[2px] border-[1px] border-input" /> {I18n.get('rv2EmptyState')}</span>
        <span className="flex items-center gap-1"><span className="size-2.5 rounded-[2px] border-[1px] border-red-500 bg-red-50" /> {I18n.get('rv2NeededToPublish')}</span>
      </div>
    </div>
  );
}

/** How far along the sheet is, in the chosen style, with the toggle between the two. */
export function ProgressCard({ progress }: { progress: SheetProgress }) {
  const [style, setStyle] = useState<ProgressStyle>(rememberedStyle);
  const choose = (next: ProgressStyle) => {
    setStyle(next);
    try {
      window.localStorage.setItem(STYLE_KEY, next);
    } catch {
      // private window: the choice lasts for this page
    }
  };
  const percent = progress.total ? Math.round((progress.filled / progress.total) * 100) : 100;
  return (
    <section aria-label={I18n.get('rv2Progress')} className="rounded-xl border-[1px] bg-background px-[1rem] py-[0.75rem]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{I18n.get('rv2InputsFilled', { filled: String(progress.filled), total: String(progress.total) })}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground tabular-nums">{percent}%</span>
          {/* for comparing the two styles; one goes before this ships */}
          <div role="radiogroup" aria-label={I18n.get('rv2ProgressStyle')} className="flex rounded-md bg-muted p-0.5">
            {([['list', List], ['grid', LayoutGrid]] as const).map(([value, Icon]) => (
              <button key={value} type="button" role="radio" aria-checked={style === value} aria-label={I18n.get(value === 'list' ? 'rv2ProgressList' : 'rv2ProgressGrid')} title={I18n.get(value === 'list' ? 'rv2ProgressList' : 'rv2ProgressGrid')}
                onClick={() => choose(value)} className={cn('flex size-6 items-center justify-center rounded', style === value ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                <Icon className="size-3.5" />
              </button>
            ))}
          </div>
        </div>
      </div>
      {style === 'list' && (
        <div className="my-2 h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-sky-700 transition-[width]" style={{ width: `${percent}%` }} />
        </div>
      )}
      <div className={style === 'grid' ? 'mt-[0.75rem]' : ''}>
        {style === 'list' ? <ProgressList progress={progress} /> : <ProgressGrid progress={progress} />}
      </div>
    </section>
  );
}

export interface PublishBlocker {
  key: string;
  label: string;
  /** stops publishing; otherwise only worth knowing */
  blocking: boolean;
  onSelect: () => void;
}

/** What still stands between the report and publishing, each a link to where it is filled in. */
export function BeforePublishCard({ blockers }: { blockers: PublishBlocker[] }) {
  return (
    <section aria-label={I18n.get('rv2BeforePublish')} className="rounded-xl border-[1px] bg-background px-[1rem] py-[0.75rem]">
      <h2 className="mb-2 text-sm font-semibold">{I18n.get('rv2BeforePublish')}</h2>
      {blockers.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="size-4" /> {I18n.get('rv2ReadyToPublish')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {blockers.slice(0, 8).map((b) => (
            <li key={b.key}>
              <button type="button" onClick={b.onSelect} className="flex w-full items-start gap-2 rounded-md px-1 py-1 text-left text-sm hover:bg-muted">
                {b.blocking ? <AlertCircle className="mt-0.5 size-4 shrink-0 text-red-600" /> : <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
                <span className="min-w-0">{b.label}</span>
              </button>
            </li>
          ))}
          {blockers.length > 8 && <li className="px-1 text-xs text-muted-foreground">{I18n.get('rv2AndMore', { count: String(blockers.length - 8) })}</li>}
        </ul>
      )}
    </section>
  );
}
